import { useState, useEffect } from 'react';
import type { FC, ElementType } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Drawer, List } from '@material-ui/core';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic';
import LocalAtmIcon from '@material-ui/icons/LocalAtm';
import GroupsIcon from '@material-ui/icons/Groups';
import StoreIcon from '@material-ui/icons/Store';
import PersonIcon from '@material-ui/icons/Person';
import PublicIcon from '@material-ui/icons/Public';
import { ShoppingBag as ShoppingBagIcon } from '../icons/shopping-bag';
import { CustomUsers as UsersIcon } from '../icons/custom-users';
import { DashboardNavbarMenuItem } from './dashboard-navbar-menu-item';
import { ReceiptTax as ReceiptTaxIcon } from '../icons/receipt-tax';
import { Roles } from '../enums/role-based-auth-guard';
import { useAuth } from '../hooks/use-auth';

interface DashboardNavbarMenuProps {
  onClose: () => void;
  open: boolean;
}

interface Item {
  icon: ElementType;
  title: string;
  href?: string;
  external?: boolean;
  accessTo?: Array<string>;
  identifier: string;
  items?: Array<{ href: string; title: string; }>
}

const items: Item[] = [
  {
    icon: EqualizerIcon,
    title: 'Dashboard',
    href: '/dashboard',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER, Roles.TECHNICIAN, Roles.DEALER],
    identifier: 'dashboard'
  },
  {
    icon: ShoppingBagIcon,
    title: 'Masters',
    href: '/dashboard/masters',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'masters'
  },
  {
    icon: GroupsIcon,
    title: 'Channel Partners',
    href: '/dashboard/channel-partners/main/invited',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'channel-partners'
  },
  {
    icon: ShoppingBagIcon,
    title: 'My Profile',
    href: '/dashboard/my-profile',
    accessTo: [Roles.CHANNEL_PARTNER],
    identifier: 'my-profile'
  },
  {
    icon: PersonIcon,
    title: 'My Profile',
    href: '/dashboard/my-profile-dealer',
    accessTo: [Roles.DEALER],
    identifier: 'my-profile'
  },
  {
    icon: ReceiptTaxIcon,
    title: 'Technicians',
    href: '/dashboard/technicians/main/pending',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER],
    identifier: 'technicians'
  },
  {
    icon: UsersIcon,
    title: 'Customers',
    href: '/dashboard/customers/search',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'customers',
    items: [
      {
        href: '/dashboard/customers/search',
        title: 'Search'
      },
      {
        href: '/dashboard/customers/delete',
        title: 'Delete'
      },
    ]
  },
  {
    icon: StoreIcon,
    title: 'Dealers',
    href: '/dashboard/dealers',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'dealers',
  },
  {
    icon: LocalAtmIcon,
    title: 'Payments',
    href: '/dashboard/payments',
    accessTo: [Roles.WEB_ADMIN, Roles.CHANNEL_PARTNER],
    identifier: 'payments'
  },
  {
    icon: LocalAtmIcon,
    title: 'Reward Points',
    href: '/dashboard/reward-points',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'rewardPoints'
    // Add items: [] if necessary
  },
  {
    icon: HeadsetMicIcon,
    title: 'Escalations',
    href: '/dashboard/escalations/',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'escalations'
  },
  {
    icon: PublicIcon,
    title: 'Web',
    href: '#',
    accessTo: [Roles.WEB_ADMIN],
    identifier: 'web',
    items: [
      {
        href: '/dashboard/statistics',
        title: 'Statistics',
      },
      {
        href: '/dashboard/ewaste-drives',
        title: 'E Waste Drives',
      },
    ],
  },
];

export const DashboardNavbarMenu: FC<DashboardNavbarMenuProps> = (props) => {
  const { open, onClose } = props;
  const { pathname } = useLocation();
  const [openedItem, setOpenedItem] = useState<Item | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(null);
  const [activeHref, setActiveHref] = useState('');
  const auth = useAuth();

  const handleOpenItem = (item: Item): void => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };

  useEffect(() => {
    items.forEach((item) => {
      // set the main element in menu bar active(this is only for mobile view)
      const active = !!pathname.toLocaleLowerCase().includes(item.identifier);
      if (active) {
        setActiveItem(item);
        setActiveHref(item.href);
        setOpenedItem(item);
      }
    });
  }, [pathname]);

  return (
    <Drawer
      anchor="top"
      onClose={onClose}
      open={open}
      transitionDuration={0}
      ModalProps={{
        BackdropProps: {
          invisible: true
        }
      }}
      PaperProps={{
        sx: {
          backgroundColor: 'primary.dark',
          color: '#B2B7C8',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100%',
          width: '100vw',
        }
      }}
    >
      <List sx={{ p: 0 }}>
        {(
          items.map((item) => (item?.accessTo?.includes(auth.user.scope)) && (
            <DashboardNavbarMenuItem
              active={activeItem?.title === item.title}
              activeHref={activeHref}
              key={item.title}
              onClose={onClose}
              onOpenItem={() => handleOpenItem(item)}
              open={openedItem?.title === item.title}
              {...item}
            />
          ))
        )}
      </List>
    </Drawer>
  );
};

DashboardNavbarMenu.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func
};
